<template>
  <v-navigation-drawer
    v-model="panel.visible"
    v-click-outside="clickedOutside"
    fixed
    right
    temporary
    stateless
    disable-resize-watcher
    width="512"
    @input="panelInputChanged"
  >
    <v-container>
      <h3 class="text-center">Edycja pliku: {{ file.name }}</h3>
      <v-divider class="my-4" />
      <v-form v-if="file" ref="form">
        <v-text-field
          v-model="form.name"
          label="Nazwa pliku"
          name="name"
          type="text"
          :error-messages="nameErrors"
          @input="$v.form.name.$touch()"
          @blur="$v.form.name.$touch()"
          @keydown.enter="submit"
        />
        <v-file-input
          v-model="fileUpload.value"
          label="Plik"
          clearable
          :error-messages="urlErrors"
          :loading="fileUpload.loading"
          @change="uploadFile"
          @click:clear="resetFile"
          @keydown.enter="submit"
        ></v-file-input>
        <v-checkbox v-model="form.isRequired" label="Wymagany do zaliczenia"></v-checkbox>
      </v-form>
      <v-divider class="my-4" />
      <v-row align="center" justify="center">
        <v-col>
          <v-btn
            class="action-btn"
            color="success"
            block
            :loading="loading"
            :disabled="fileUpload.loading"
            @click="submit"
            >Zapisz zmiany</v-btn
          >
        </v-col>
        <v-col>
          <v-dialog v-model="dialog" persistent max-width="400">
            <template v-slot:activator="{ on }">
              <v-btn
                class="action-btn"
                color="error"
                block
                :disabled="loading"
                v-on="formChanged ? on : { click: cancel }"
                >Anuluj</v-btn
              >
            </template>
            <v-card>
              <v-toolbar color="error" dark flat>
                <v-toolbar-title>Ostrzeżenie</v-toolbar-title>
              </v-toolbar>
              <v-card-text class="mt-4">Czy na pewno chcesz anulować wprowadzone zmiany?</v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="text--secondary" text @click="cancelWarning">Nie</v-btn>
                <v-btn color="error" text @click="cancel">Tak</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { httpsUrl } from '@/plugins/vuelidate/validators';
import MediaApi from '@/api/mediaApi';
import ClickOutside from 'vue-click-outside';
import { createNamespacedHelpers } from 'vuex';

const { mapActions, mapState } = createNamespacedHelpers('courses');
const { mapState: mapAccountState } = createNamespacedHelpers('account');

export default {
  directives: {
    ClickOutside
  },
  mixins: [validationMixin],
  data: function() {
    return {
      form: {
        name: '',
        url: '',
        isRequired: false
      },
      dialog: false,
      loading: false,
      fileUpload: {
        value: null,
        loading: false
      },
      panel: {
        visible: true,
        firstLoad: false
      }
    };
  },
  computed: {
    ...mapState({
      file: state => state.activeCourseEditTree.activeElement.element,
      courseId: state => state.activeCourseEditTree.course.id,
      definition: state => state.activeCourseEditTree.activeElement.definition
    }),
    ...mapAccountState(['token']),
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) {
        return errors;
      }
      if (!this.$v.form.name.required) {
        errors.push('Nazwa pliku jest wymagana');
      }
      return errors;
    },
    urlErrors() {
      const errors = [];
      if (!this.$v.form.url.$dirty) {
        return errors;
      }
      if (!this.$v.form.url.required) {
        errors.push('Plik jest wymagany');
      }
      return errors;
    },
    formValid() {
      return !this.$v.form.$invalid && !this.$v.form.$pending;
    },
    formChanged() {
      return this.$v.$anyDirty;
    }
  },
  async mounted() {
    this.form.name = this.file.name;
    this.form.isRequired = this.file.isRequired;
    if (!this.file.newDefinition) {
      if (this.file.id === -1) {
        return;
      }

      await this.requestElementDefinition({ id: this.file.id });
      this.form.url = this.definition.url;
    } else {
      this.form.url = this.file.newDefinition.url;
    }

    if (this.form.url) {
      this.fileUpload.value = new File([], this.form.url.split('/').pop());
    }
  },
  validations: {
    form: {
      name: {
        required
      },
      url: { required, httpsUrl }
    }
  },
  methods: {
    ...mapActions(['saveFile', 'resetActiveElement', 'requestElementDefinition']),
    async submit() {
      this.$v.$touch();

      if (this.formValid) {
        this.loading = true;
        await this.saveFile({
          courseId: this.courseId,
          instance: this.file,
          name: this.form.name,
          isRequired: this.form.isRequired,
          definition: this.definition,
          newDefinition: {
            url: this.form.url
          }
        });
        this.loading = false;
      }
    },
    cancel() {
      this.dialog = false;
      this.resetActiveElement();
    },
    async uploadFile(file) {
      if (file) {
        this.fileUpload.loading = true;
        try {
          const response = await new MediaApi(this.token).uploadFile(file);
          this.form.url = response.data.result.uri;
        } catch (err) {
          console.error(err);
          // TODO: handle
        } finally {
          this.fileUpload.loading = false;
        }

        this.$v.form.url.$touch();
      }
    },
    resetFile() {
      this.form.url = '';
      this.$v.form.url.$touch();
    },
    clickedOutside() {
      if (this.panel.firstLoad) {
        if (this.formChanged) {
          this.dialog = true;
        } else {
          this.cancel();
        }
      }

      this.panel.firstLoad = true;
    },
    panelInputChanged() {
      this.panel.firstLoad = false;
    },
    cancelWarning() {
      this.panel.firstLoad = false;
      this.dialog = false;
    }
  }
};
</script>
